import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  ViewChild,
  ElementRef,
  OnDestroy,
  inject,
  NgZone,
  ChangeDetectionStrategy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { trigger, transition, style, animate } from '@angular/animations';
import { IconComponentV2 } from '../icon-component/icon.component';
import { WhitelabelChatService } from '../../../core/services/whitelabel-chat.service';
import { toast } from 'ngx-sonner';

/** Minimal bot context for the workspace chat shell (list / preview). */
export interface ChatWorkspaceBotContext {
  pid: number;
  name: string;
  productName: string;
}

export interface WorkspaceChatMessage {
  id: string;
  role: 'user' | 'assistant';
  text: string;
}

@Component({
  selector: 'app-chat-workspace-interface',
  standalone: true,
  imports: [CommonModule, FormsModule, IconComponentV2],
  templateUrl: './chat-workspace-interface.component.html',
  styles: [
    `
      .chat-workspace-scroll {
        scrollbar-width: thin;
        scrollbar-color: rgba(124, 58, 237, 0.35) transparent;
      }
      .chat-workspace-scroll::-webkit-scrollbar {
        width: 6px;
      }
      .chat-workspace-scroll::-webkit-scrollbar-thumb {
        border-radius: 9999px;
        background: rgba(124, 58, 237, 0.35);
      }
      .chat-workspace-scroll::-webkit-scrollbar-track {
        background: transparent;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [
    trigger('backdropFade', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('220ms cubic-bezier(0.25, 0.8, 0.25, 1)', style({ opacity: 1 })),
      ]),
      transition(':leave', [
        animate('180ms cubic-bezier(0.4, 0, 1, 1)', style({ opacity: 0 })),
      ]),
    ]),
    trigger('panelFromCorner', [
      transition(':enter', [
        style({
          opacity: 0,
          transform: 'translate(100%, 1.25rem) scale(0.96)',
        }),
        animate(
          '400ms cubic-bezier(0.22, 1, 0.36, 1)',
          style({
            opacity: 1,
            transform: 'translate(0, 0) scale(1)',
          }),
        ),
      ]),
      transition(':leave', [
        animate(
          '300ms cubic-bezier(0.4, 0, 1, 1)',
          style({
            opacity: 0,
            transform: 'translate(100%, 0.75rem) scale(0.98)',
          }),
        ),
      ]),
    ]),
  ],
})
export class ChatWorkspaceInterfaceComponent implements OnChanges, OnDestroy {
  private whitelabelChat = inject(WhitelabelChatService);
  private ngZone = inject(NgZone);

  @Input() open = false;
  @Input() bot: ChatWorkspaceBotContext | null = null;

  /** Suggested starters shown until the user sends their first message. */
  @Input() quickPrompts: string[] = [
    'What can you help me with?',
    'Summarize the essentials',
    'Walk me through next steps',
  ];

  @Input() inputPlaceholder = 'Message…';

  @Output() closed = new EventEmitter<void>();

  @ViewChild('messageScroll') private messageScroll?: ElementRef<HTMLDivElement>;
  @ViewChild('composer') private composer?: ElementRef<HTMLTextAreaElement>;

  draft = '';
  messages: WorkspaceChatMessage[] = [];

  /** True while waiting for the whitelabel chat API (stream or static). */
  isSending = false;
  isUploadingDocument = false;

  private readonly maxLen = 4000;
  private workspaceSessionId = '';
  private abortChat: AbortController | null = null;

  ngOnChanges(changes: SimpleChanges): void {
    const openCh = changes['open'];
    if (
      this.open &&
      this.bot &&
      openCh?.currentValue === true &&
      (openCh.previousValue === false || openCh.firstChange)
    ) {
      this.resetThread();
    }
  }

  close(): void {
    this.abortChat?.abort();
    this.closed.emit();
  }

  ngOnDestroy(): void {
    this.abortChat?.abort();
  }

  onBackdropClick(): void {
    this.close();
  }

  trackMessage(_index: number, msg: WorkspaceChatMessage): string {
    return msg.id;
  }

  get showQuickPrompts(): boolean {
    return this.messages.every((m) => m.role === 'assistant');
  }

  sendFromPrompt(text: string): void {
    this.draft = text;
    this.send();
  }

  /**
   * Uploads selected documents into the current workspace chat session.
   */
  async uploadSessionDocuments(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    input.value = '';

    if (!files.length || !this.bot || this.isUploadingDocument) {
      return;
    }

    this.isUploadingDocument = true;
    try {
      const result = await this.whitelabelChat.uploadSessionDocuments(
        this.bot.pid,
        this.ensureWorkspaceSessionId(),
        files,
      );
      const documents = result.documents;
      this.messages = [
        ...this.messages,
        {
          id: `a-upload-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
          role: 'assistant',
          text: `Uploaded ${documents.length} document(s) successfully.`,
        },
      ];
      this.queueScrollToBottom();
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Document upload failed. Please try again.';
      toast.error(message);
    } finally {
      this.isUploadingDocument = false;
    }
  }

  onComposerKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.send();
    }
  }

  adjustComposerHeight(event: Event): void {
    const el = event.target as HTMLTextAreaElement;
    el.style.height = 'auto';
    const next = Math.min(el.scrollHeight, 120);
    el.style.height = `${next}px`;
  }

  send(): void {
    const text = this.draft.replace(/\r\n/g, '\n').trim();
    if (!text || !this.bot || this.isSending) {
      return;
    }
    const clipped = text.length > this.maxLen ? text.slice(0, this.maxLen) : text;
    const assistantId = `a-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

    this.messages = [
      ...this.messages,
      {
        id: `u-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
        role: 'user',
        text: clipped,
      },
      {
        id: assistantId,
        role: 'assistant',
        text: '',
      },
    ];
    this.draft = '';
    if (this.composer?.nativeElement) {
      this.composer.nativeElement.style.height = 'auto';
    }
    this.queueScrollToBottom();

    this.abortChat?.abort();
    this.abortChat = new AbortController();
    this.isSending = true;

    const sessionId = this.ensureWorkspaceSessionId();

    void this.whitelabelChat
      .sendMessage(
        clipped,
        this.bot.pid,
        sessionId,
        {
          onDelta: (chunk) => {
            this.ngZone.run(() => {
              this.appendToLastAssistant(chunk);
              this.queueScrollToBottom();
            });
          },
          onComplete: (payload) => {
            this.ngZone.run(() => {
              this.setLastAssistantText(payload.answer);
              this.isSending = false;
              this.queueScrollToBottom();
            });
          },
          onError: (err) => {
            this.ngZone.run(() => {
              this.isSending = false;
              this.setLastAssistantText(
                `Sorry — something went wrong. ${err.message || 'Please try again.'}`,
              );
              toast.error(err.message || 'Chat request failed');
            });
          },
        },
        this.abortChat.signal,
      ).finally(() => {
        this.ngZone.run(() => {
          if (this.abortChat?.signal.aborted) {
            this.isSending = false;
          }
        });
      });
  }

  isLastAssistantStreaming(index: number): boolean {
    const msg = this.messages[index];
    if (!msg || msg.role !== 'assistant') {
      return false;
    }
    if (index !== this.messages.length - 1) {
      return false;
    }
    return this.isSending && msg.text.length === 0;
  }

  private appendToLastAssistant(chunk: string): void {
    const msgs = [...this.messages];
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].role === 'assistant') {
        msgs[i] = { ...msgs[i], text: msgs[i].text + chunk };
        this.messages = msgs;
        return;
      }
    }
  }

  private setLastAssistantText(text: string): void {
    const msgs = [...this.messages];
    for (let i = msgs.length - 1; i >= 0; i--) {
      if (msgs[i].role === 'assistant') {
        msgs[i] = { ...msgs[i], text };
        this.messages = msgs;
        return;
      }
    }
  }

  private resetThread(): void {
    if (!this.bot) {
      return;
    }
    this.abortChat?.abort();
    this.workspaceSessionId = '';
    this.isSending = false;
    this.draft = '';
    this.messages = [
      {
        id: `a-welcome-${this.bot.pid}`,
        role: 'assistant',
        text: `Hi — I’m ${this.bot.name}. How can I help you today?`,
      },
    ];
    if (this.composer?.nativeElement) {
      this.composer.nativeElement.style.height = 'auto';
    }
  }

  /**
   * Creates or returns the session id shared by document uploads and messages.
   */
  private ensureWorkspaceSessionId(): string {
    if (!this.bot) {
      return this.workspaceSessionId;
    }
    if (!this.workspaceSessionId) {
      this.workspaceSessionId = `session_${this.bot.pid}_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
    }
    return this.workspaceSessionId;
  }

  private queueScrollToBottom(): void {
    setTimeout(() => {
      const el = this.messageScroll?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    }, 0);
  }
}
